import { useState, useRef, useCallback } from 'react';
import type { CSSProperties } from 'react';
import { uploadFile } from '../utils/api';
import type { ProjectMedia, MediaType } from '../types';

interface MediaUploaderProps {
  media: ProjectMedia[];
  onMediaAdd: (media: { mediaType: MediaType; url: string; cloudflareKey: string }) => void;
  onMediaRemove: (mediaId: string) => void;
  disabled?: boolean;
  singleUpload?: boolean;
}

export default function MediaUploader({
  media,
  onMediaAdd,
  onMediaRemove,
  disabled = false,
  singleUpload = false,
}: MediaUploaderProps) {
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = useCallback(async (files: FileList | null) => {
    if (!files || files.length === 0 || disabled) return;

    const file = files[0];
    const validTypes = [
      'image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp',
      'video/mp4', 'video/webm', 'video/quicktime'
    ];

    if (!validTypes.includes(file.type)) {
      setError('Invalid file type. Supported: JPEG, PNG, GIF, WebP, MP4, WebM, MOV');
      return;
    }

    // Max 50MB
    if (file.size > 50 * 1024 * 1024) {
      setError('File too large. Maximum size is 50MB.');
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setError(null);

    try {
      // Upload via backend (all uploads go through backend)
      setUploadProgress(20);
      const response = await uploadFile(file, 'projects');
      setUploadProgress(80);

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to upload file');
      }

      const { url, key, mediaType } = response.data;

      // Notify parent
      onMediaAdd({
        mediaType,
        url,
        cloudflareKey: key,
      });

      setUploadProgress(100);
    } catch (err) {
      console.error('Upload failed:', err);
      setError(err instanceof Error ? err.message : 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  }, [disabled, onMediaAdd]);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    handleFileSelect(e.dataTransfer.files);
  }, [handleFileSelect]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  }, []);

  const handleClick = () => {
    if (!disabled && !uploading) {
      fileInputRef.current?.click();
    }
  };

  const dropzoneStyle: CSSProperties = {
    padding: '24px',
    borderRadius: '12px',
    border: `2px dashed ${dragOver ? '#14F195' : 'rgba(255,255,255,0.1)'}`,
    background: dragOver ? 'rgba(20, 241, 149, 0.05)' : 'rgba(255,255,255,0.02)',
    textAlign: 'center',
    cursor: disabled || uploading ? 'not-allowed' : 'pointer',
    transition: 'all 0.2s',
    opacity: disabled ? 0.5 : 1,
  };

  return (
    <div>
      {/* Upload Zone */}
      <div
        style={dropzoneStyle}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={handleClick}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp,video/mp4,video/webm,video/quicktime"
          style={{ display: 'none' }}
          onChange={(e) => handleFileSelect(e.target.files)}
          disabled={disabled || uploading}
        />

        {uploading ? (
          <div>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 12px',
              borderRadius: '50%',
              border: '3px solid rgba(20, 241, 149, 0.2)',
              borderTopColor: '#14F195',
              animation: 'spin 1s linear infinite',
            }} />
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
              Uploading... {uploadProgress}%
            </p>
          </div>
        ) : (
          <div>
            <div style={{
              width: '48px',
              height: '48px',
              margin: '0 auto 12px',
              borderRadius: '12px',
              background: 'rgba(255,255,255,0.05)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: '20px',
            }}>
              📷
            </div>
            <p style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px', fontWeight: 500 }}>
              Drop files here or click to upload
            </p>
            <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px', marginTop: '4px' }}>
              Images (JPEG, PNG, GIF, WebP) or Videos (MP4, WebM, MOV)
            </p>
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '11px', marginTop: '2px' }}>
              Max 50MB per file
            </p>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          marginTop: '12px',
          padding: '12px',
          background: 'rgba(239, 68, 68, 0.1)',
          border: '1px solid rgba(239, 68, 68, 0.3)',
          borderRadius: '8px',
          color: '#f87171',
          fontSize: '13px',
        }}>
          {error}
        </div>
      )}

      {/* Media Preview Grid */}
      {!singleUpload && media.length > 0 && (
        <div style={{ marginTop: '16px' }}>
          <div style={{
            fontSize: '13px',
            fontWeight: 500,
            color: 'rgba(255,255,255,0.7)',
            marginBottom: '8px',
          }}>
            Uploaded Media ({media.length})
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
            gap: '8px',
          }}>
            {media.map((item) => (
              <div
                key={item.id}
                style={{
                  position: 'relative',
                  aspectRatio: '1',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  background: 'rgba(255,255,255,0.05)',
                }}
              >
                {item.mediaType === 'image' ? (
                  <img
                    src={item.url}
                    alt=""
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: 'rgba(0,0,0,0.5)',
                  }}>
                    <span style={{ fontSize: '24px' }}>🎬</span>
                  </div>
                )}

                {/* Remove Button */}
                {!disabled && (
                  <button
                    onClick={() => onMediaRemove(item.id)}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      width: '24px',
                      height: '24px',
                      borderRadius: '6px',
                      background: 'rgba(0,0,0,0.7)',
                      border: 'none',
                      color: 'white',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '14px',
                    }}
                  >
                    ×
                  </button>
                )}

                {/* Type Badge */}
                <div style={{
                  position: 'absolute',
                  bottom: '4px',
                  left: '4px',
                  padding: '2px 6px',
                  borderRadius: '4px',
                  background: 'rgba(0,0,0,0.7)',
                  fontSize: '10px',
                  color: 'white',
                  textTransform: 'uppercase',
                }}>
                  {item.mediaType}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
